import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowsClockwise, CloudSlash, Warning } from "@phosphor-icons/react";
import { abarbeiten, beobachten, verwerfen, vorgemerkt, type Vorgemerkt } from "../lib/warteschlange";
import { Dialog } from "./Bausteine";

/** Beim Abmelden: den Datenspeicher des Service Workers leeren (public/sw.js). */
export function offlineSpeicherLeeren(): void {
  navigator.serviceWorker?.controller?.postMessage("abmelden");
}

/**
 * In der Kopfleiste (SPEC §15.3): „offline", solange kein Netz da ist, und wie viel offline
 * Erfasstes noch auf die Übertragung wartet. Überträgt von selbst beim Start, wenn das
 * Netz zurückkommt, und minütlich, solange etwas wartet.
 */
export function Netzstand() {
  const qc = useQueryClient();
  const [online, setOnline] = useState(navigator.onLine);
  const [liste, setListe] = useState<Vorgemerkt[]>([]);
  const [fehler, setFehler] = useState<{ e: Vorgemerkt; text: string }[]>([]);
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    const neu = () => { vorgemerkt().then(setListe); };
    const los = async () => {
      const r = await abarbeiten();
      setFehler(r.fehler);
      if (r.uebertragen) for (const k of [["maengel"], ["fotos"], ["begehung"], ["besprechung"], ["uebersicht"]]) qc.invalidateQueries({ queryKey: k });
      neu();
    };
    const an = () => { setOnline(true); los(); };
    const aus = () => setOnline(false);
    window.addEventListener("online", an);
    window.addEventListener("offline", aus);
    const weg = beobachten(neu);
    los();
    const takt = setInterval(() => { if (navigator.onLine) los(); }, 60_000);
    return () => { window.removeEventListener("online", an); window.removeEventListener("offline", aus); weg(); clearInterval(takt); };
  }, [qc]);

  if (online && liste.length === 0) return null;
  return (
    <>
      <button className={`knopf leise netzstand${online ? "" : " aus"}`} onClick={() => setOffen(true)}
              title={online ? "Offline Erfasstes wird übertragen" : "Kein Netz – angezeigt wird der zuletzt geladene Stand"}>
        {online ? <ArrowsClockwise size={16} /> : <CloudSlash size={16} />}
        {!online && "offline"}{liste.length > 0 && ` · ${liste.length} wartet`}
        {fehler.length > 0 && <Warning size={14} />}
      </button>
      {offen && (
        <Dialog offen zu={() => setOffen(false)} titel="Offline erfasst" fuss={<>
          <button className="knopf" disabled={!online} onClick={async () => { const r = await abarbeiten(); setFehler(r.fehler); setListe(await vorgemerkt()); }}>
            <ArrowsClockwise size={16} /> Jetzt übertragen</button>
          <button className="knopf" onClick={() => setOffen(false)}>Schließen</button>
        </>}>
          <p style={{ marginTop: 0 }}>{online
            ? "Das Netz ist da. Was hier steht, wird gerade übertragen oder ist an einem Fehler hängen geblieben."
            : "Ohne Netz zeigt die App den zuletzt geladenen Stand. Neue Mängel und Fotos werden auf diesem Gerät gespeichert und übertragen, sobald wieder Netz da ist."}</p>
          <div className="liste">
            {liste.map((e) => {
              const f = fehler.find((x) => x.e.id === e.id);
              return (
                <div key={e.id} className="zeile" style={{ minHeight: 44 }}>
                  <span className="nr">{e.art === "mangel" ? "Mangel" : "Foto"}</span>
                  <div className="haupt"><div>{e.titel}</div>
                    <div className="mute">{new Date(e.am).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                      {e.bilder.length > 0 && ` · ${e.bilder.length} Foto${e.bilder.length > 1 ? "s" : ""}`}
                      {f && <span style={{ color: "var(--error)" }}> · {f.text}</span>}</div></div>
                  {f && <button className="knopf leise gefahr" onClick={() => confirm("Diesen Eintrag verwerfen? Er ist dann verloren.") && verwerfen(e.id)}>Verwerfen</button>}
                </div>
              );
            })}
            {liste.length === 0 && <div className="leer">Nichts wartet.</div>}
          </div>
        </Dialog>
      )}
    </>
  );
}
